import React, { FC, useMemo } from 'react'
import { CrewClaim, ClaimStatus } from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

type Props = {
    visible: boolean
    claim: CrewClaim | null
    onClose: () => void
    onViewAttachments?: () => void
}

const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const LABEL_SMALL = 'fw-semibold text-muted d-block mb-1'

const getStatusDisplay = (status: ClaimStatus) => {
    switch (status) {
        case 'DRAFT':
            return { label: 'Draft', badgeClass: 'badge-light-secondary' }
        case 'SUBMITTED':
            return { label: 'Submitted', badgeClass: 'badge-light-warning' }
        case 'REVIEWED':
            return { label: 'Reviewed', badgeClass: 'badge-light-success' }
        case 'REJECTED':
            return { label: 'Rejected', badgeClass: 'badge-light-danger' }
        default:
            return { label: String(status), badgeClass: 'badge-light' }
    }
}

const formatCurrency = (value: number) =>
    value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    } catch {
        return dateStr
    }
}

const formatClaimMonth = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', {
            month: 'long',
            year: 'numeric',
        })
    } catch {
        return dateStr
    }
}

const resolveCurrencyCode = (salaryCurrency?: string | null, salaryOtherCurrency?: string | null) => {
    const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
    const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
    if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
    return normalizedAccountCurrency
}

const getCurrencySymbol = (currencyCode?: string) => {
    const code = (currencyCode || '').toString().trim().toUpperCase()
    switch (code) {
        case 'USD':
            return '$'
        case 'EUR':
            return '\u20AC'
        case 'GBP':
            return '\u00A3'
        case 'SGD':
            return 'S$'
        case 'INR':
            return '\u20B9'
        case 'AED':
            return '\u062F.\u0625 '
        default:
            try {
                const part = new Intl.NumberFormat('en', {
                    style: 'currency',
                    currency: code,
                    currencyDisplay: 'narrowSymbol',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                }).formatToParts(0).find((p) => p.type === 'currency')
                const maybeSymbol = (part?.value || '').trim()
                return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
            } catch {
                return ''
            }
    }
}

export const ViewCrewClaimModal: FC<Props> = ({
    visible,
    claim,
    onClose,
    onViewAttachments,
}) => {
    const statusMeta = useMemo(() => {
        if (!claim) return { label: '-', badgeClass: 'badge-light' }
        return getStatusDisplay(claim.status)
    }, [claim])

    const totalAmount = useMemo(() => {
        if (!claim) return 0
        return (
            (claim.medical || 0) +
            (claim.safetyGear || 0) +
            (claim.travel || 0) +
            (claim.boardingLodging || 0) +
            (claim.hraAllowance || 0) +
            (claim.bonus || 0) +
            (claim.miscReimbursements || 0) +
            (claim.overtimeAmount || 0)
        )
    }, [claim])

    const formulaValues = useMemo(() => {
        if (!claim) {
            return {
                bfAmount: 0,
                wagesEarned: 0,
                crewClaims: 0,
                crewDeduction: 0,
                balanceWages: 0,
                currencySymbol: '',
            }
        }

        const toAmount = (value: unknown) => {
            const n = Number(value)
            return Number.isFinite(n) ? n : 0
        }

        const bfAmount = toAmount(claim.bfAmount)
        const wagesEarned = toAmount(claim.wagesEarned)
        const crewClaims = totalAmount
        const crewDeduction = toAmount(claim.deductionsTotal)
        const computedBalance = bfAmount + wagesEarned + crewClaims - crewDeduction
        const balanceWages = toAmount(claim.balanceCf ?? computedBalance)
        const currencyCode = resolveCurrencyCode(
            claim.salaryCurrency ?? claim.accountCurrency ?? null,
            claim.salaryOtherCurrency ?? claim.otherCurrency ?? null
        )

        return {
            bfAmount,
            wagesEarned,
            crewClaims,
            crewDeduction,
            balanceWages,
            currencySymbol: getCurrencySymbol(currencyCode),
        }
    }, [claim, totalAmount])

    if (!visible || !claim) return null

    return (
        <div style={WRAP_STYLE} tabIndex={-1}>
            <div
                className='bg-white text-dark rounded shadow-lg d-flex flex-column'
                style={{
                    width: '75vw',
                    height: '75vh',
                    maxWidth: '1100px',
                    maxHeight: '800px',
                }}
            >
                {/* Header */}
                <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
                    <div className='d-flex flex-column'>
                        <h5 className='modal-title text-dark m-6'>
                            Crew Claim – {claim.crewName || `Claim #${claim.id}`}
                        </h5>
                        <div className='text-muted fs-7 mx-6'>
                            Rank:&nbsp;
                            <span className='fw-semibold'>
                                {claim.rankName || '-'}
                            </span>
                            {claim.vesselName && (
                                <>
                                    <span className='mx-2'>•</span>
                                    Vessel:&nbsp;
                                    <VesselLandingLink
                                        vesselId={claim.vesselId ?? null}
                                        vesselName={claim.vesselName}
                                        className='fw-semibold text-dark text-hover-primary'
                                    />
                                </>
                            )}
                            {claim.claimMonth && (
                                <>
                                    <span className='mx-2'>•</span>
                                    Claim Month:&nbsp;
                                    <span className='fw-semibold'>
                                        {formatClaimMonth(claim.claimMonth)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className='d-flex align-items-center gap-3'>
                        <span className={`badge ${statusMeta.badgeClass}`}>
                            {statusMeta.label}
                        </span>

                        <button
                            type='button'
                            className='btn-close m-6'
                            aria-label='Close'
                            onClick={onClose}
                        />
                    </div>
                </div>

                {/* Body with scroll */}
                <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
                    <div className='row g-4'>
                        {/* Claim Details Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Claim Details</h6>

                            <div className='row'>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Crew Name</label>
                                    <div className='fw-semibold'>{claim.crewName || '-'}</div>
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Rank</label>
                                    <div>{claim.rankName || '-'}</div>
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <label className={LABEL_SMALL}>Claim Month</label>
                                    <div>{formatClaimMonth(claim.claimMonth)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Financial Details Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Financial Details</h6>

                            <div className='row'>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Salary</label>
                                    <div className='fw-semibold'>{formatCurrency(claim.salary || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Medical</label>
                                    <div>{formatCurrency(claim.medical || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Travel</label>
                                    <div>{formatCurrency(claim.travel || 0)}</div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Boarding & Lodging</label>
                                    <div>{formatCurrency(claim.boardingLodging || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>HRA Allowance</label>
                                    <div>{formatCurrency(claim.hraAllowance || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Bonus</label>
                                    <div>{formatCurrency(claim.bonus || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Misc. Reimbursements</label>
                                    <div>{formatCurrency(claim.miscReimbursements || 0)}</div>
                                </div>
                                <div className='col-md-3 mb-3'>
                                    <label className={LABEL_SMALL}>Overtime Amount</label>
                                    <div>{formatCurrency(claim.overtimeAmount || 0)}</div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='border rounded p-3 bg-light-primary'>
                                        <label className={LABEL_SMALL}>Crew Claims</label>
                                        <div className='fs-3 fw-bold text-primary'>{formatCurrency(totalAmount)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className='row mt-3'>
                                <div className='col-12'>
                                    <div className='border rounded p-3 bg-light'>
                                        <div className='fw-semibold text-dark mb-2'>
                                            B/F Amount + Wages Earned + Crew Claims - Crew Deduction = Balance Wages
                                        </div>
                                        <div className='table-responsive border rounded'>
                                            <table className='table table-bordered align-middle mb-0'>
                                                <thead>
                                                    <tr>
                                                        <th className='fw-semibold text-dark bg-light text-nowrap'>B/F Amount</th>
                                                        <th className='fw-semibold text-dark bg-light text-nowrap'>Wages Earned</th>
                                                        <th className='fw-semibold text-dark bg-light text-nowrap'>Crew Claims</th>
                                                        <th className='fw-semibold text-dark bg-light text-nowrap'>Crew Deduction</th>
                                                        <th className='fw-semibold text-dark bg-light text-nowrap'>Balance Wages</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>{`${formulaValues.currencySymbol}${formatCurrency(formulaValues.bfAmount)}`}</td>
                                                        <td>{`${formulaValues.currencySymbol}${formatCurrency(formulaValues.wagesEarned)}`}</td>
                                                        <td>{`${formulaValues.currencySymbol}${formatCurrency(formulaValues.crewClaims)}`}</td>
                                                        <td>{`${formulaValues.currencySymbol}${formatCurrency(formulaValues.crewDeduction)}`}</td>
                                                        <td className='fw-bold text-primary'>{`${formulaValues.currencySymbol}${formatCurrency(formulaValues.balanceWages)}`}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remarks Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Remarks</h6>
                            <div
                                className='border rounded p-3 bg-light'
                                style={{ minHeight: '60px', whiteSpace: 'pre-wrap' }}
                            >
                                {claim.remarks || 'No remarks provided.'}
                            </div>
                            <div
                                className='border rounded p-3 bg-light mt-3'
                                style={{ minHeight: '60px', whiteSpace: 'pre-wrap' }}
                            >
                                <span className='fw-semibold d-block mb-2'>Office Remarks</span>
                                {claim.officeRemarks || 'No office remarks provided.'}
                            </div>
                        </div>

                        {/* Status & Audit Info Section */}
                        <div className='col-12'>
                            <h6 className='text-primary mb-3 border-bottom pb-2'>Status & History</h6>
                            <div className='row'>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Status</label>
                                    <div>
                                        <span className={`badge ${statusMeta.badgeClass}`}>
                                            {statusMeta.label}
                                        </span>
                                    </div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Submitted At</label>
                                    <div>{formatDate(claim.submittedAt)}</div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Reviewed At</label>
                                    <div>{formatDate(claim.reviewedAt)}</div>
                                </div>
                                <div className='col-md-3 mb-2'>
                                    <label className={LABEL_SMALL}>Reviewed By</label>
                                    <div>{claim.reviewedByName || '-'}</div>
                                </div>
                            </div>

                            {claim.status === 'REJECTED' && claim.rejectionReason && (
                                <div className='row mt-2'>
                                    <div className='col-12'>
                                        <label className={LABEL_SMALL}>Rejection Reason</label>
                                        <div
                                            className='border rounded p-2 bg-light-danger text-danger'
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {claim.rejectionReason}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='row mt-3'>
                                <div className='col-md-6 mb-2'>
                                    <label className={LABEL_SMALL}>Created At</label>
                                    <div>{formatDate(claim.createdAt)}</div>
                                </div>
                                <div className='col-md-6 mb-2'>
                                    <label className={LABEL_SMALL}>Last Updated</label>
                                    <div>{formatDate(claim.updatedAt)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
                    <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ViewCrewClaimModal
